import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import apiService from '../services/apiService';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('nexus_token'));
    const [loading, setLoading] = useState(true);
    const [logoutMessage, setLogoutMessage] = useState(null);

    const logout = useCallback((message) => {
        localStorage.removeItem('nexus_token');
        localStorage.removeItem('nexus_refresh_token');
        localStorage.removeItem('nexus_user');
        setToken(null);
        setUser(null);
        if (message) {
            setLogoutMessage(message);
        }
    }, []);

    useEffect(() => {
        // Register apiService logout callback
        apiService.onLogout((reason) => {
            logout(reason);
        });
        apiService.onSessionRefresh(({ role, roles, permissions }) => {
            setUser(prev => {
                if (!prev) return prev;
                const updated = { ...prev, role, roles: roles || [], permissions };
                localStorage.setItem('nexus_user', JSON.stringify(updated));
                return updated;
            });
        });

        if (token) {
            const userData = localStorage.getItem('nexus_user');
            if (userData) {
                try {
                    setUser(JSON.parse(userData));
                } catch (e) {
                    console.error('Failed to parse user data:', e);
                    logout();
                }
            }
            // Enrich with full profile (includes phoneNumber)
            authService.profile.get()
                .then(result => {
                    const profile = result?.data || result;
                    if (profile && profile.firstName) {
                        setUser(prev => {
                            const updated = {
                                ...prev,
                                firstName: profile.firstName,
                                lastName: profile.lastName,
                                phoneNumber: profile.phoneNumber || '',
                            };
                            localStorage.setItem('nexus_user', JSON.stringify(updated));
                            return updated;
                        });
                    }
                })
                .catch(err => {
                    console.error('Profile enrichment failed:', err);
                });
        }
        setLoading(false);
    }, []);

    // Cross-tab logout sync: listen for storage changes
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key === 'nexus_token' && e.newValue === null && token) {
                // Token was removed in another tab — log out here too
                setToken(null);
                setUser(null);
                setLogoutMessage('You have been logged out in another tab.');
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [token]);

    const setSession = (loginData) => {
        const userData = {
            email: loginData.email,
            firstName: loginData.firstName,
            lastName: loginData.lastName,
            role: loginData.role,
            roles: loginData.roles || [],
            permissions: loginData.permissions || [],
            phoneNumber: loginData.phoneNumber || '',
            passwordChangeRequired: loginData.passwordChangeRequired || false,
            passwordAgeDays: loginData.passwordAgeDays || 0,
        };
        localStorage.setItem('nexus_token', loginData.token);
        if (loginData.refreshToken) {
            localStorage.setItem('nexus_refresh_token', loginData.refreshToken);
        }
        localStorage.setItem('nexus_user', JSON.stringify(userData));
        setToken(loginData.token);
        setUser(userData);
        setLogoutMessage(null);
    };

    const clearLogoutMessage = useCallback(() => {
        setLogoutMessage(null);
    }, []);

    const updateUser = (data) => {
        const updated = { ...user, ...data };
        setUser(updated);
        localStorage.setItem('nexus_user', JSON.stringify(updated));
    };

    const hasPermission = useCallback((perm) => {
        if (!perm) return true;
        if (user?.role === 'SUPERADMIN' || user?.roles?.includes('SUPERADMIN')) return true;
        return user?.permissions?.includes(perm) || false;
    }, [user]);

    return (
        <AuthContext.Provider value={{
            user,
            token,
            setSession,
            logout,
            updateUser,
            hasPermission,
            loading,
            logoutMessage,
            clearLogoutMessage
        }}>
            {children}
        </AuthContext.Provider>
    );
};
